/**
 * Utility functions for step reordering in runbooks.
 * Used by both EditRunbook and ReviewDraftRunbook components.
 */

/**
 * Reorders steps by swapping a step at `index` with its neighbor in `direction`.
 * Returns null if the move is invalid (boundary violations, insufficient steps).
 *
 * @param {Array} steps - Current steps array (each step has step_number, step_description, etc.)
 * @param {Object} imageUrls - Map of step_number → image URL
 * @param {number} index - Index of the step to move (0-based)
 * @param {'up'|'down'} direction - Direction to move
 * @returns {{ steps: Array, imageUrls: Object, newIndex: number } | null}
 */
export function reorderStep(steps, imageUrls, index, direction) {
  if (!steps || steps.length < 2) return null;

  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= steps.length) return null;

  // Swap the two steps
  const newSteps = [...steps];
  const temp = newSteps[index];
  newSteps[index] = newSteps[targetIndex];
  newSteps[targetIndex] = temp;

  // Renumber all steps sequentially (1-based)
  const renumberedSteps = newSteps.map((step, idx) => ({
    ...step,
    step_number: idx + 1,
  }));

  // Remap image URLs for the swapped positions
  const updatedImageUrls = { ...imageUrls };
  const oldStepNum = steps[index].step_number;
  const targetStepNum = steps[targetIndex].step_number;
  const urlA = imageUrls[oldStepNum];
  const urlB = imageUrls[targetStepNum];

  delete updatedImageUrls[oldStepNum];
  delete updatedImageUrls[targetStepNum];
  if (urlA) updatedImageUrls[targetIndex + 1] = urlA;
  if (urlB) updatedImageUrls[index + 1] = urlB;

  return {
    steps: renumberedSteps,
    imageUrls: updatedImageUrls,
    newIndex: targetIndex,
  };
}

/**
 * Checks if a step at a given index can be moved in a direction.
 *
 * @param {number} index - 0-based index of the step
 * @param {number} totalSteps - Total number of steps
 * @param {'up'|'down'} direction - Direction to move
 * @returns {boolean}
 */
export function canMoveStep(index, totalSteps, direction) {
  if (totalSteps < 2) return false;
  if (direction === 'up') return index > 0;
  if (direction === 'down') return index < totalSteps - 1;
  return false;
}
